import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  alpha
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Security,
  Notifications,
  Save,
  Business,
  ContentCopy,
  CheckCircle,
  AutoAwesome,
  Extension,
  Category,
  AccessTime,
  Block as BlockIcon
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { systemSettingsService } from '../../../../shared/services/SystemSettingsService'
import { setDisplayTimeZone } from '../../../../shared/utils/datetime'
import { organisationsService } from '../../../../shared/services/OrganisationsService'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import TeamsIntegration from '../../components/settings/TeamsIntegration'
import { BlockedSenders } from '../../components/settings/BlockedSenders'
import ZapierIntegration from '../../components/settings/ZapierIntegration'
import CategoryManagement from '../../components/settings/CategoryManagement'
import { MfaSetupComponent } from '../../components/settings/MfaSetupComponent'

interface GeneralSettings {
  // Regional Settings
  timeZone: string
  dateFormat: string
  timeFormat: string
  defaultLanguage: string
  firstDayOfWeek: number
  // Business Hours
  businessHoursStart: string
  businessHoursEnd: string
  workingDays: string
  // Ticket Settings
  autoCloseInactiveTicketsAfterDays: number
  defaultTicketPriority: string
  ticketNumberPrefix: string
  // Portal Settings
  enableCustomerPortalRegistration: boolean
  // Attachment Settings
  maxAttachmentSizeMB: number
  allowedFileTypes: string
}


interface NotificationSettings {
  emailNotifications: boolean
  teamsNotifications: boolean
  slackNotifications: boolean
  teamsWebhookUrl?: string
  slackWebhookUrl?: string
}

interface SecuritySettings {
  sessionTimeout: number
  passwordMinLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumbers: boolean
  requireSpecialChars: boolean
  enableTwoFactor: boolean
}

interface SlaSettings {
  policies: {
    [key: string]: {
      responseHours: number
      resolutionHours: number
    }
  }
}

interface Organization {
  customerId: string
  companyName: string
  contactEmail: string
  emailAddress?: string
  userCount: number
  ticketCount: number
  isActive: boolean
  createdAt: string
  tenantSlug: string
  parentOrganizationId?: string
  childSites?: Organization[]
}

const generalSchema = yup.object({
  timeZone: yup.string().required('Time zone is required'),
  dateFormat: yup.string().required('Date format is required'),
  timeFormat: yup.string().required('Time format is required'),
  defaultLanguage: yup.string().required('Default language is required'),
  firstDayOfWeek: yup.number().min(0).max(6).required('First day of week is required'),
  businessHoursStart: yup.string().required('Business hours start is required').matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  businessHoursEnd: yup.string().required('Business hours end is required').matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  workingDays: yup.string().required('Working days are required'),
  autoCloseInactiveTicketsAfterDays: yup.number().min(0).max(365).required('Auto-close days is required'),
  defaultTicketPriority: yup.string().required('Default ticket priority is required'),
  ticketNumberPrefix: yup.string().required('Ticket number prefix is required').max(10, 'Prefix too long'),
  enableCustomerPortalRegistration: yup.boolean().required(),
  maxAttachmentSizeMB: yup.number().min(1).max(100).required('Max attachment size is required'),
  allowedFileTypes: yup.string().required('Allowed file types are required')
})


function AdminSettings() {
  const theme = useTheme()
  const [searchParams] = useSearchParams()

  // Check URL parameter for default tab
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab')
    const tabMap: Record<string, number> = {
      'general': 0,
      'emails': 1,
      'notifications': 2,
      'security': 3,
      'categories': 4,
      'sla': 5,
      'integrations': 6,
      'blockedsenders': 7
    }
    return tabParam ? (tabMap[tabParam.toLowerCase()] ?? 0) : 0
  }

  const [activeTab, setActiveTab] = useState(getInitialTab())
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form instances for each settings section
  const generalForm = useForm<GeneralSettings>({
    resolver: yupResolver(generalSchema),
    defaultValues: {
      timeZone: 'Europe/London',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      defaultLanguage: 'en',
      firstDayOfWeek: 0,
      businessHoursStart: '09:00',
      businessHoursEnd: '17:00',
      workingDays: 'Monday,Tuesday,Wednesday,Thursday,Friday',
      autoCloseInactiveTicketsAfterDays: 0,
      defaultTicketPriority: 'Medium',
      ticketNumberPrefix: 'TKT-',
      enableCustomerPortalRegistration: true,
      maxAttachmentSizeMB: 10,
      allowedFileTypes: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.zip'
    }
  })

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    teamsNotifications: false,
    slackNotifications: false,
    teamsWebhookUrl: '',
    slackWebhookUrl: ''
  })

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    sessionTimeout: 30,
    passwordMinLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    enableTwoFactor: false
  })

  const slaForm = useForm<SlaSettings>({
    defaultValues: {
      policies: {
        'Urgent': { responseHours: 1, resolutionHours: 4 },
        'High': { responseHours: 2, resolutionHours: 8 },
        'Medium': { responseHours: 4, resolutionHours: 24 },
        'Low': { responseHours: 8, resolutionHours: 48 }
      }
    }
  })

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)

  useEffect(() => {
    loadSettings()
    loadOrganizations()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setLoadError(null)
      const settings = await systemSettingsService.getSystemSettings()

      // Reset forms with loaded values if settings are available
      if (settings?.general) {
        generalForm.reset(settings.general)
      }
      if (settings?.notifications) {
        setNotificationSettings(settings.notifications)
      }
      if (settings?.security) {
        setSecuritySettings(settings.security)
      }
      if (settings?.sla) {
        slaForm.reset(settings.sla)
      }
    } catch (err: any) {
      console.error('Failed to load settings:', err)
      // Don't show error to user - just use default values
      // This allows the settings page to work even if the backend endpoint doesn't exist yet
      console.log('Using default settings values')
    } finally {
      setLoading(false)
    }
  }

  const loadOrganizations = async () => {
    try {
      // Fetch both Organisations (customer orgs) and ChildSites
      const [organisations, allSites] = await Promise.all([
        organisationsService.getOrganisations(),
        OrganizationService.getOrganizations()
      ])

      // OrganizationService.getOrganizations() returns BOTH organisations and child sites
      // Filter to get only actual child sites (not organisations)
      const childSites = allSites.filter(site => site.type !== 'Organisation')

      // Convert Organisations to the format we need
      const orgsData = organisations.map(org => ({
        customerId: org.customerId,
        companyName: org.companyName,
        contactEmail: org.contactEmail,
        emailAddress: org.emailAddress, // Use EmailCode-based address from backend
        userCount: org.userCount,
        ticketCount: org.ticketCount,
        isActive: org.isActive,
        createdAt: org.createdAt,
        tenantSlug: org.tenantSlug,
        parentOrganizationId: undefined // Organisations don't have parents
      }))

      // Filter out "Taylin Digital" as it's the tenant owner (shown separately)
      const customerOrgs = orgsData.filter(org => {
        const isTaylinDigital = org.companyName.toLowerCase().trim().includes('taylin')
        return !isTaylinDigital
      })

      // Attach child sites to their parent organisations
      const orgsWithChildren = customerOrgs.map(org => {
        // Find ChildSites that belong to this Organisation (not nested child sites)
        const children = childSites
          .filter(cs => cs.customerId === org.customerId && !cs.parentOrganizationId)
          .map(cs => ({
            customerId: cs.childSiteId || cs.customerId,
            companyName: cs.name,
            contactEmail: cs.contactEmail || '',
            emailAddress: cs.emailAddress, // Use EmailCode-based address from backend
            userCount: cs.userCount || 0,
            ticketCount: cs.ticketCount || 0,
            isActive: cs.isActive,
            createdAt: cs.createdAt || '',
            tenantSlug: cs.slug || '',
            parentOrganizationId: org.customerId
          }))

        return {
          ...org,
          childSites: children
        }
      })

      setOrganizations(orgsWithChildren)
    } catch (err: any) {
      console.error('Failed to load organizations:', err)
    }
  }

  const generateSupportEmail = (orgName: string) => {
    // Generate org slug from organization name
    const orgSlug = orgName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')

    return `${orgSlug}@tickets.taylindigital.com`
  }

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email)
      setCopiedEmail(email)
      setTimeout(() => setCopiedEmail(null), 2000)
    } catch (err) {
      console.error('Failed to copy email address')
    }
  }

  const handleSaveSettings = async (section: string) => {
    setSaveStatus('saving')
    try {
      switch (section) {
        case 'general':
          await systemSettingsService.updateGeneralSettings(generalForm.getValues())
          // Apply the new timezone immediately so dates re-render without a reload.
          setDisplayTimeZone(generalForm.getValues().timeZone)
          break
        case 'notifications':
          await systemSettingsService.updateNotificationSettings(notificationSettings)
          break
        case 'security':
          await systemSettingsService.updateSecuritySettings(securitySettings)
          break
        case 'sla':
          await systemSettingsService.updateSlaSettings(slaForm.getValues())
          break
      }

      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error: any) {
      console.error('Failed to save settings:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  const tabConfig = [
    { label: 'General', icon: <SettingsIcon /> },
    { label: 'Support Emails', icon: <AutoAwesome /> },
    { label: 'Notifications', icon: <Notifications /> },
    { label: 'Security', icon: <Security /> },
    { label: 'Categories', icon: <Category /> },
    { label: 'SLA Policies', icon: <AccessTime /> },
    { label: 'Integrations', icon: <Extension /> },
    { label: 'Blocked Senders', icon: <BlockIcon /> }
  ]

  const timeZones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney'
  ]

  const dateFormats = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'DD-MMM-YYYY'
  ]

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' }
  ]

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          System Settings
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure your helpdesk system preferences and integrations
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {saveStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to save settings. Please try again.
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            {tabConfig.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {/* General Settings */}
          {activeTab === 0 && (
            <form onSubmit={generalForm.handleSubmit(() => handleSaveSettings('general'))}>
              {/* Regional Settings */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Regional Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure timezone, date/time formats, and language preferences
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="timeZone"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Time Zone</InputLabel>
                        <Select {...field} label="Time Zone">
                          {timeZones.map((tz) => (
                            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="dateFormat"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Date Format</InputLabel>
                        <Select {...field} label="Date Format">
                          {dateFormats.map((format) => (
                            <MenuItem key={format} value={format}>{format}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="timeFormat"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Time Format</InputLabel>
                        <Select {...field} label="Time Format">
                          <MenuItem value="12h">12-hour (e.g., 2:30 PM)</MenuItem>
                          <MenuItem value="24h">24-hour (e.g., 14:30)</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="defaultLanguage"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Default Language</InputLabel>
                        <Select {...field} label="Default Language">
                          {languages.map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>{lang.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="firstDayOfWeek"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>First Day of Week</InputLabel>
                        <Select {...field} label="First Day of Week">
                          <MenuItem value={0}>Sunday</MenuItem>
                          <MenuItem value={1}>Monday</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Business Hours */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Business Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set your standard business hours and working days
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Hours Start"
                    placeholder="09:00"
                    {...generalForm.register('businessHoursStart')}
                    error={!!generalForm.formState.errors.businessHoursStart}
                    helperText={generalForm.formState.errors.businessHoursStart?.message || 'Format: HH:MM (24-hour)'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Hours End"
                    placeholder="17:00"
                    {...generalForm.register('businessHoursEnd')}
                    error={!!generalForm.formState.errors.businessHoursEnd}
                    helperText={generalForm.formState.errors.businessHoursEnd?.message || 'Format: HH:MM (24-hour)'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Working Days"
                    {...generalForm.register('workingDays')}
                    error={!!generalForm.formState.errors.workingDays}
                    helperText={generalForm.formState.errors.workingDays?.message || 'Comma-separated list (e.g., Monday,Tuesday,Wednesday,Thursday,Friday)'}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Ticket Settings */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Ticket Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure default ticket behavior and numbering
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Auto-Close Inactive Tickets After (Days)"
                    {...generalForm.register('autoCloseInactiveTicketsAfterDays', { valueAsNumber: true })}
                    error={!!generalForm.formState.errors.autoCloseInactiveTicketsAfterDays}
                    helperText={generalForm.formState.errors.autoCloseInactiveTicketsAfterDays?.message || '0 = disabled'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="defaultTicketPriority"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Default Ticket Priority</InputLabel>
                        <Select {...field} label="Default Ticket Priority">
                          <MenuItem value="Low">Low</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Critical">Critical</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ticket Number Prefix"
                    {...generalForm.register('ticketNumberPrefix')}
                    error={!!generalForm.formState.errors.ticketNumberPrefix}
                    helperText={generalForm.formState.errors.ticketNumberPrefix?.message || 'e.g., TKT-, HD-'}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Portal Settings */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Portal Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control customer portal features
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="enableCustomerPortalRegistration"
                    control={generalForm.control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Enable Customer Portal Registration"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Attachment Settings */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Attachment Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure file upload restrictions
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Attachment Size (MB)"
                    {...generalForm.register('maxAttachmentSizeMB', { valueAsNumber: true })}
                    error={!!generalForm.formState.errors.maxAttachmentSizeMB}
                    helperText={generalForm.formState.errors.maxAttachmentSizeMB?.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Allowed File Types"
                    {...generalForm.register('allowedFileTypes')}
                    error={!!generalForm.formState.errors.allowedFileTypes}
                    helperText={generalForm.formState.errors.allowedFileTypes?.message || 'Comma-separated file extensions (e.g., .pdf,.doc,.jpg)'}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveStatus === 'saving'}
                >
                  Save General Settings
                </Button>
              </Box>
            </form>
          )}

          {/* Support Emails */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Organisation Support Email Addresses
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Each organisation has a unique support email address. Customers can email these addresses to automatically create support tickets.
              </Typography>

              {organizations.length === 0 ? (
                <Alert severity="info">
                  No organisations found. Create an organisation to generate a support email address.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {/* Tenant Owner */}
                  <Grid item xs={12}>
                    <Card sx={{ background: alpha(theme.palette.success.main, 0.05), borderLeft: `4px solid ${theme.palette.success.main}` }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: theme.palette.success.main,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Business />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Taylin Digital
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  bgcolor: theme.palette.success.main,
                                  color: 'white',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontWeight: 'bold'
                                }}
                              >
                                TENANT OWNER
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              Main tenant account
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <AutoAwesome sx={{ color: theme.palette.success.main }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Support Email Address
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 'bold'
                              }}
                            >
                              {generateSupportEmail('Taylin Digital')}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={copiedEmail === generateSupportEmail('Taylin Digital') ? <CheckCircle /> : <ContentCopy />}
                            onClick={() => handleCopyEmail(generateSupportEmail('Taylin Digital'))}
                            color={copiedEmail === generateSupportEmail('Taylin Digital') ? 'success' : 'primary'}
                          >
                            {copiedEmail === generateSupportEmail('Taylin Digital') ? 'Copied!' : 'Copy'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Customer Organizations */}
                  {organizations.map((org) => {
                    // Use EmailCode-based address from backend, fallback to slug-based if not available
                    const supportEmail = org.emailAddress || generateSupportEmail(org.companyName)
                    return (
                      <Grid item xs={12} key={org.customerId}>
                        {/* Parent Organization */}
                        <Card sx={{ background: alpha(theme.palette.primary.main, 0.02) }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2,
                                  bgcolor: theme.palette.primary.main,
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <Business />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {org.companyName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {org.userCount} users · {org.ticketCount} tickets
                                </Typography>
                              </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <AutoAwesome sx={{ color: theme.palette.success.main }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Support Email Address
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {supportEmail}
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={copiedEmail === supportEmail ? <CheckCircle /> : <ContentCopy />}
                                onClick={() => handleCopyEmail(supportEmail)}
                                color={copiedEmail === supportEmail ? 'success' : 'primary'}
                              >
                                {copiedEmail === supportEmail ? 'Copied!' : 'Copy'}
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>

                        {/* Child Sites (indented) */}
                        {org.childSites && org.childSites.length > 0 && (
                          <Box sx={{ ml: 6, mt: 2 }}>
                            {org.childSites.map((childSite) => {
                              // Use EmailCode-based address from backend, fallback to slug-based if not available
                              const childSupportEmail = childSite.emailAddress || generateSupportEmail(childSite.companyName)
                              return (
                                <Card
                                  key={childSite.customerId}
                                  sx={{
                                    background: alpha(theme.palette.secondary.main, 0.02),
                                    mb: 2,
                                    borderLeft: `4px solid ${theme.palette.secondary.main}`
                                  }}
                                >
                                  <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                      <Box
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: 2,
                                          bgcolor: theme.palette.secondary.main,
                                          color: 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0
                                        }}
                                      >
                                        <Business fontSize="small" />
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                          {childSite.companyName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {childSite.userCount} users · {childSite.ticketCount} tickets · Child Site
                                        </Typography>
                                      </Box>
                                    </Box>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <AutoAwesome fontSize="small" sx={{ color: theme.palette.success.main }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Support Email Address
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {childSupportEmail}
                                        </Typography>
                                      </Box>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={copiedEmail === childSupportEmail ? <CheckCircle /> : <ContentCopy />}
                                        onClick={() => handleCopyEmail(childSupportEmail)}
                                        color={copiedEmail === childSupportEmail ? 'success' : 'primary'}
                                      >
                                        {copiedEmail === childSupportEmail ? 'Copied!' : 'Copy'}
                                      </Button>
                                    </Box>
                                  </CardContent>
                                </Card>
                              )
                            })}
                          </Box>
                        )}
                      </Grid>
                    )
                  })}
                </Grid>
              )}

              <Alert severity="info" sx={{ mt: 3 }}>
                <strong>How it works:</strong> When customers email these addresses, their messages automatically create tickets assigned to the respective organisation. Email routing is handled by the system based on the organisation slug in the email address.
              </Alert>
            </Box>
          )}

          {/* Notification Settings */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Email Notification Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure email notifications for ticket events
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.emailNotifications}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          emailNotifications: e.target.checked
                        }))}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">Enable Email Notifications</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Send email notifications to customers and agents when tickets are created, updated, or resolved
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Looking for Microsoft Teams, Zapier, or other integrations?
                </Typography>
                <Typography variant="body2">
                  Third-party integrations have been moved to the <strong>Integrations</strong> tab for better organization.
                  Click on the Integrations tab above to configure Microsoft Teams, Zapier, and other app connections.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={() => handleSaveSettings('notifications')}
                  disabled={saveStatus === 'saving'}
                >
                  Save Notification Settings
                </Button>
              </Box>
            </Box>
          )}

          {/* Security Settings */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Security Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set up multi-factor authentication to secure your account
              </Typography>

              <MfaSetupComponent />
            </Box>
          )}

          {/* Categories */}
          {activeTab === 4 && (
            <CategoryManagement />
          )}

          {/* SLA Policies */}
          {activeTab === 5 && (
            <form onSubmit={slaForm.handleSubmit(() => handleSaveSettings('sla'))}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  SLA Policy Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure response and resolution time targets for each priority level
                </Typography>

                <Grid container spacing={3}>
                  {['Urgent', 'High', 'Medium', 'Low'].map((priority) => (
                    <Grid item xs={12} md={6} key={priority}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {priority} Priority
                          </Typography>
                          <Controller
                            name={`policies.${priority}.responseHours` as any}
                            control={slaForm.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Response Time (hours)"
                                type="number"
                                fullWidth
                                margin="normal"
                                inputProps={{ min: 0.1, max: 168, step: 0.1 }}
                              />
                            )}
                          />
                          <Controller
                            name={`policies.${priority}.resolutionHours` as any}
                            control={slaForm.control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Resolution Time (hours)"
                                type="number"
                                fullWidth
                                margin="normal"
                                inputProps={{ min: 0.1, max: 720, step: 0.1 }}
                              />
                            )}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saveStatus === 'saving' ? <CircularProgress size={20} /> : <Save />}
                    disabled={saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving' ? 'Saving...' : 'Save SLA Settings'}
                  </Button>
                  {saveStatus === 'success' && (
                    <Alert severity="success" sx={{ flex: 1 }}>
                      SLA settings saved successfully
                    </Alert>
                  )}
                  {saveStatus === 'error' && (
                    <Alert severity="error" sx={{ flex: 1 }}>
                      Failed to save SLA settings
                    </Alert>
                  )}
                </Box>
              </Box>
            </form>
          )}

          {/* Integrations */}
          {activeTab === 6 && (
            <Box>
              <TeamsIntegration />
              <Divider sx={{ my: 6 }} />
              <ZapierIntegration />
            </Box>
          )}

          {/* Blocked Senders */}
          {activeTab === 7 && (
            <BlockedSenders />
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default AdminSettings